import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
    BAND_COLOR,
    BAND_THRESHOLDS,
    BANDS,
    BandKey,
    EmptyNote,
    StudentRow,
    WidgetRow,
    bandOf,
} from './schoolDashboard';

/**
 * The drill-down behind "Students by risk band": who is in each band, where,
 * and why they were flagged.
 *
 * Built from two blocks of one response -- the `students_at_risk` rows (keyed
 * on user_id, metrics only) and the roster (names and sections). Nothing here
 * computes a metric; it names and sorts what the semantic layer returned.
 */

/** One student, with their metrics if they have any. */
export interface BandEntry {
    user_id: number;
    full_name: string;
    roll_number: string | null;
    section_label: string;
    band: BandKey;
    accuracy: number | null;
    answered: number;
    missed: number;
    completion: number | null;
    flags: string[];
}

/** Fewer than this share of set tests completed reads as "missing tests". */
const COMPLETION_FLOOR = 75;

const num = (v: WidgetRow[string] | undefined) =>
    v === null || v === undefined ? null : Number(v);

/**
 * Why a student is where they are. Two reasons with two different fixes:
 * low accuracy is a teaching problem, missed tests are an attendance one --
 * and a student can have both.
 */
function flagsOf(band: BandKey, missed: number, completion: number | null): string[] {
    const flags: string[] = [];
    if (band === 'nodata') flags.push('Barely attempted');
    if (band === 'intervention') flags.push('Low accuracy');
    if (missed >= 2 || (completion !== null && completion < COMPLETION_FLOOR)) {
        flags.push('Missing tests');
    }
    return flags;
}

/**
 * Every rostered student, banded.
 *
 * The roster, not the metric rows, is the list. A student with no activity in
 * the window has no metric row at all, and dropping them would hide exactly
 * the child who has stopped turning up; they land in "not enough data".
 * A metric row with no roster entry (a student since removed) is kept too,
 * under their id, so the counts still match what the API measured.
 */
export function bandEntries(rows: WidgetRow[], students: StudentRow[]): BandEntry[] {
    const byUser = new Map<number, WidgetRow>();
    rows.forEach(r => byUser.set(Number(r.user_id), r));

    const build = (userId: number, s: StudentRow | undefined): BandEntry => {
        const row = byUser.get(userId);
        const band = row ? bandOf(row) : 'nodata';
        const missed = Number(row?.assignments_missed ?? 0);
        const completion = num(row?.completion_rate);
        return {
            user_id: userId,
            full_name: s?.full_name || `Student #${userId}`,
            roll_number: s?.roll_number ?? null,
            section_label: s?.section_label || '—',
            band,
            accuracy: num(row?.accuracy_pct),
            answered: Number(row?.questions_attempted ?? 0),
            missed,
            completion,
            flags: flagsOf(band, missed, completion),
        };
    };

    const seen = new Set<number>();
    const out = students.map(s => {
        seen.add(Number(s.user_id));
        return build(Number(s.user_id), s);
    });
    rows.forEach(r => {
        const id = Number(r.user_id);
        if (!seen.has(id)) out.push(build(id, undefined));
    });
    return out;
}

export function entryCounts(entries: BandEntry[]): Record<BandKey, number> {
    const out: Record<BandKey, number> = { healthy: 0, watch: 0, intervention: 0, nodata: 0 };
    entries.forEach(e => {
        out[e.band] += 1;
    });
    return out;
}

// ---------------------------------------------------------------------------
// Per-section bars
// ---------------------------------------------------------------------------

/**
 * The same bands, one thin bar per section, so a principal can see WHERE the
 * problem sits before opening anyone's name. Every segment opens the list for
 * that band in that section; the label opens the whole section.
 */
export const SectionBandBars = ({
    entries,
    onSelect,
}: {
    entries: BandEntry[];
    onSelect: (band: BandKey | 'all', section: string) => void;
}) => {
    const theme = useTheme();

    const sections = useMemo(() => {
        const map = new Map<string, BandEntry[]>();
        entries.forEach(e => {
            if (!map.has(e.section_label)) map.set(e.section_label, []);
            map.get(e.section_label)!.push(e);
        });
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
    }, [entries]);

    if (sections.length < 2) return null;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                By section
            </Typography>
            <Stack spacing={1}>
                {sections.map(([label, list]) => {
                    const counts = entryCounts(list);
                    const needHelp = counts.intervention;
                    return (
                        <Stack key={label} direction="row" spacing={1.5} alignItems="center">
                            <Tooltip title={`Open all of ${label}`}>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={label}
                                    onClick={() => onSelect('all', label)}
                                    sx={{ minWidth: 56, fontWeight: 700 }}
                                />
                            </Tooltip>
                            <Stack
                                direction="row"
                                sx={{ flex: 1, height: 10, borderRadius: 5, overflow: 'hidden' }}
                            >
                                {BANDS.filter(b => counts[b.key] > 0).map(b => (
                                    <Tooltip
                                        key={b.key}
                                        title={`${label} · ${b.label}: ${counts[b.key]} — click to see who`}
                                    >
                                        <Box
                                            onClick={() => onSelect(b.key, label)}
                                            sx={{
                                                width: `${(counts[b.key] / list.length) * 100}%`,
                                                bgcolor: theme.palette[BAND_COLOR[b.key]].main,
                                                opacity: b.key === 'nodata' ? 0.35 : 1,
                                                cursor: 'pointer',
                                                '&:hover': { filter: 'brightness(0.9)' },
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                            <Typography
                                variant="caption"
                                sx={{
                                    minWidth: 110,
                                    textAlign: 'right',
                                    fontWeight: needHelp > 0 ? 700 : 400,
                                    color: needHelp > 0 ? theme.palette.error.main : 'text.secondary',
                                }}
                            >
                                {needHelp > 0 ? `${needHelp} need help` : 'none need help'} · {list.length}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// The student list
// ---------------------------------------------------------------------------

const csvCell = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Worst first: lowest accuracy, and among the unmeasured, least answered. */
function byConcern(a: BandEntry, b: BandEntry) {
    if (a.accuracy === null && b.accuracy === null) return a.answered - b.answered;
    if (a.accuracy === null) return 1;
    if (b.accuracy === null) return -1;
    return a.accuracy - b.accuracy;
}

export const BandStudentsDrawer = ({
    open,
    onClose,
    entries,
    initialBand,
    initialSection,
}: {
    open: boolean;
    onClose: () => void;
    entries: BandEntry[];
    initialBand: BandKey | 'all';
    initialSection: string;
}) => {
    const theme = useTheme();
    const [band, setBand] = useState<BandKey | 'all'>(initialBand);
    const [section, setSection] = useState(initialSection);
    const [search, setSearch] = useState('');

    // Each open starts from what was clicked, not from the last visit.
    useEffect(() => {
        if (open) {
            setBand(initialBand);
            setSection(initialSection);
            setSearch('');
        }
    }, [open, initialBand, initialSection]);

    const sections = useMemo(
        () =>
            Array.from(new Set(entries.map(e => e.section_label))).sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true })
            ),
        [entries]
    );

    // Band counts follow the section and search, so each chip says how many
    // it would show.
    const inScope = useMemo(() => {
        const q = search.trim().toLowerCase();
        return entries.filter(
            e =>
                (section === '' || e.section_label === section) &&
                (q === '' || e.full_name.toLowerCase().includes(q) || (e.roll_number || '').toLowerCase().includes(q))
        );
    }, [entries, section, search]);

    const shown = useMemo(
        () => inScope.filter(e => band === 'all' || e.band === band).sort(byConcern),
        [inScope, band]
    );

    const bandInfo = BANDS.find(b => b.key === band);

    const downloadCsv = () => {
        const head = ['Roll', 'Name', 'Section', 'Band', 'Accuracy %', 'Answered', 'Tests missed', 'Completion %', 'Flags'];
        const rows = shown.map(e => [
            e.roll_number,
            e.full_name,
            e.section_label,
            BANDS.find(b => b.key === e.band)?.label,
            e.accuracy,
            e.answered,
            e.missed,
            e.completion,
            e.flags.join('; '),
        ]);
        const csv = [head, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        a.download = `students_${band}${section ? '_' + section : ''}.csv`.replace(/[^\w.-]+/g, '_');
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 680 }, maxWidth: '100%', display: 'flex', flexDirection: 'column' },
            }}
        >
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {band === 'all' ? 'All students' : bandInfo?.label}
                            {section ? ` · ${section}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {bandInfo ? `${bandInfo.hint}. ` : ''}Assigned work only, last 30 days. Worst
                            first.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Stack direction="row" spacing={0.75} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 0.75 }}>
                    <Chip
                        size="small"
                        label={`All ${inScope.length}`}
                        color={band === 'all' ? 'primary' : 'default'}
                        variant={band === 'all' ? 'filled' : 'outlined'}
                        onClick={() => setBand('all')}
                    />
                    {BANDS.map(b => {
                        const n = inScope.filter(e => e.band === b.key).length;
                        return (
                            <Chip
                                key={b.key}
                                size="small"
                                label={`${b.label} ${n}`}
                                color={band === b.key ? BAND_COLOR[b.key] : 'default'}
                                variant={band === b.key ? 'filled' : 'outlined'}
                                onClick={() => setBand(b.key)}
                            />
                        );
                    })}
                </Stack>

                {/* Explicit widths: the theme makes text fields full-width by
                    default, which let the section select squeeze the search
                    box down to nothing. */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                    <TextField
                        select
                        size="small"
                        label="Section"
                        value={section}
                        onChange={e => setSection(e.target.value)}
                        fullWidth={false}
                        sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
                    >
                        <MenuItem value="">All sections</MenuItem>
                        {sections.map(s => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Search name or roll"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        fullWidth={false}
                        sx={{ flex: 1, minWidth: 180 }}
                    />
                    <Button
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                        startIcon={<DownloadIcon />}
                        onClick={downloadCsv}
                        disabled={shown.length === 0}
                    >
                        CSV
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
                {shown.length === 0 ? (
                    <EmptyNote>Nobody matches these filters.</EmptyNote>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Accuracy
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Answered
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    <Tooltip title="Tests set in the window that the student did not sit">
                                        <span>Missed</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    <Tooltip title="Share of set tests the student submitted">
                                        <span>Done</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Why</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map(e => {
                                const tone = theme.palette[BAND_COLOR[e.band]].main;
                                return (
                                    <TableRow key={e.user_id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {e.full_name}
                                            </Typography>
                                            {e.roll_number && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Roll {e.roll_number}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{e.section_label}</TableCell>
                                        <TableCell align="right">
                                            {e.accuracy === null || e.band === 'nodata' ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    —
                                                </Typography>
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={`${e.accuracy}%`}
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: alpha(tone, 0.14),
                                                        color: tone,
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {e.answered}
                                            {e.answered < BAND_THRESHOLDS.minAnswered && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {' '}
                                                    (&lt;{BAND_THRESHOLDS.minAnswered})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ color: e.missed > 0 ? theme.palette.error.main : undefined }}
                                        >
                                            {e.missed}
                                        </TableCell>
                                        <TableCell align="right">
                                            {e.completion === null ? '—' : `${e.completion}%`}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                                {e.flags.map(f => (
                                                    <Chip
                                                        key={f}
                                                        size="small"
                                                        variant="outlined"
                                                        color={f === 'Missing tests' ? 'warning' : 'error'}
                                                        label={f}
                                                    />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Drawer>
    );
};
